import _ from "lodash";
import MobileDetect from "mobile-detect";

/**
 * Microsoft Clarity enrichment — device environment and user identity tags.
 *
 * Clarity sessions can be filtered and searched in the Clarity dashboard using
 * any of the custom tags set here. Tags are attached to the whole recording, so
 * async tags (see setClarityDesktopEnv) arrive slightly after page init but are
 * still linked to the session.
 *
 * Cross-referencing with backend logs:
 *   - backendSessionId (user.secretGUID) — matches the SESSION_ID request header
 *   - userId (user.userId)               — matches the USER_ID request header
 *   Both headers are sent on every authenticated API call.
 *
 * Tag inventory:
 *   businessId, businessName   — which restaurant/business
 *   iosVersion, androidVersion — mobile OS version (set by MobileDetect)
 *   windowsVersion, macosVersion, chromeosVersion — desktop OS version; os/browser/device names
 *                                are captured automatically by Clarity — only version is custom
 *   chromeVersion, edgeVersion, operaVersion, firefoxVersion, safariVersion, ieVersion — browser version
 *   device                     — device model: phone/tablet name (mobile) or laptop model (desktop,
 *                                via Client Hints API on Chromium browsers)
 *   clientSessionId            — cuid, stable across navigations in one browser session
 *   backendSessionId           — secretGUID, matches SESSION_ID header in backend logs
 *   appUserId                  — matches USER_ID header; "userId" is reserved by Clarity internally
 *                                so the custom tag uses "appUserId"; also passed to clarity("identify")
 *                                to stitch recordings across sessions for the same user
 *   hasJsError                 — "true" if any unhandled JS error occurred this session
 *   jsErrorCount               — number of unhandled errors in this session (raw count)
 *   jsErrorCountBucket         — "1" | "2-3" | "4+" for dashboard filtering
 *   lastError                  — message of the most recent error (truncated to 200 chars)
 *   lastErrorUrl               — script file where the most recent error originated
 *   appMode                    — "native" | "kiosk" | "embed" | "web"
 *   networkQuality             — "4g" | "3g" | "slow-2g" | "2g" (navigator.connection; Chromium only)
 *   viewportSize               — e.g. "1280x720" (may differ from screen resolution on desktop)
 *   entrySource                — "deeplink" | "qr" | "referral" | "direct"
 *   branchId, branchName       — selected branch (set on mount if already chosen; updated on change)
 *   servingOption              — "delivery" | "pickup" | "dine-in" (updated with branch)
 *   funnelStage                — current page category, set on mount and on each route change
 *   serverResponse_ttfb        — Time to First Byte in ms (responseStart − startTime; includes DNS/TCP/TLS)
 *   serverResponseBucket_ttfb  — "good(<800ms)" | "needsWork(800-1800ms)" | "poor(1800ms+)"
 *   firstPaint_fcp             — First Contentful Paint in ms
 *   firstPaintBucket_fcp       — "good(<1.8s)" | "needsWork(1.8-3s)" | "poor(3s+)"
 *   mainContentLoad_lcp        — Largest Contentful Paint in ms
 *   mainContentLoadBucket_lcp  — "good(<2.5s)" | "needsWork(2.5-4s)" | "poor(4s+)"
 *   interactionDelay_inp       — Interaction to Next Paint in ms (worst observed interaction)
 *   interactionDelayBucket_inp — "good(<200ms)" | "needsWork(200-500ms)" | "poor(500ms+)"
 *   layoutShift_cls            — Cumulative Layout Shift × 1000 (integer; e.g. 0.12 → 120)
 *   layoutShiftBucket_cls      — "good(<0.1)" | "needsWork(0.1-0.25)" | "poor(0.25+)"
 *   domContentLoaded           — DOMContentLoaded in ms (no standard bucket threshold)
 *   smsCodeDelayed             — "true" if user spent >30s on the verify-code screen (SMS not arrived)
 *   smsResendRequested         — "true" if user tapped "Resend"
 *   smsChangePhoneRequested    — "true" if user tapped "Change phone number" (abandons verification)
 *   captchaFailed              — "true" if captcha execution threw or returned no token
 *   captchaProvider            — "hcaptcha" | "google" (which provider failed)
 *   captchaAction              — the action string passed to executeRecaptcha (e.g. "signup", "payment")
 *   captchaError               — truncated error message from the failed execution
 *   locale                     — navigator.language e.g. "en-US", "he-IL"
 *   checkoutTotal              — order total at begin-checkout (decimal, e.g. "12.50")
 *   checkoutItemCount          — number of line items at begin-checkout (raw count)
 *   checkoutItemCountBucket    — "1" | "2-3" | "4-6" | "7+" for dashboard filtering
 *   checkoutCurrency           — ISO currency code at begin-checkout
 *   orderConfirmedId           — backend order ID after successful payment
 *   orderConfirmedTotal        — final order total after successful payment (decimal)
 *   orderConfirmedItemCount    — item count in the confirmed order (raw count)
 *   orderConfirmedItemCountBucket — "1" | "2-3" | "4-6" | "7+"
 *   orderConfirmedCurrency     — ISO currency code of the confirmed order
 *   paymentFailed              — "true" once any payment failure occurs
 *   paymentFailedReason        — truncated placeOrderError message
 *   paymentFailedType          — payment method type that failed (e.g. "credit_card", "cash")
 *   lastSearchTerm             — most recent non-empty menu search query (≥2 chars, up to 100)
 *   lastAddedItem              — name of the most recently added cart item (up to 100 chars)
 *   lastAddedItemPrice         — price of the most recently added item
 *   lastAddedItemCurrency      — ISO currency code of the most recently added item
 *   tabHiddenDuringCheckout    — "true" if user switched away from tab while on the payment page
 *   orderConfirmedPaymentType  — payment method used on the successful order (e.g. "credit_card")
 *   apiHasError                — "true" if any soft (caught) API error was detected this session
 *   lastApiErrorContext        — which flow triggered the error (e.g. "saveOrder")
 *   lastApiError               — truncated error message (200 chars; Clarity tag cap is 256)
 *   promoCodeSubmitted         — "true" once any promo code submission fires (regardless of outcome)
 *   promoCodeApplied           — "true" once a promo code is successfully applied (no error)
 *   promoCodeFailed            — "true" if any promo code submission returned an error
 *   promoCodeRemoved           — "true" once any promo code is removed from the cart
 *   lastPromoCode              — most recently submitted/failed/removed promo code (up to 100 chars); applied reuses the tag set by submitted
 *   lastPromoError             — error type key of the last failure (e.g. "INVALID_CODE")
 *   customerType               — "new" | "returning" (set when latestOrders loads after login)
 *   lastRemovedItem            — name of the most recently removed cart item (up to 100 chars)
 *   lastRemovedItemPrice       — price of the most recently removed item
 *   lastRemovedItemCurrency    — ISO currency code of the most recently removed item
 *   serverHost                 — window.location.hostname at session start (e.g. "order.beengo.com",
 *                                "deiezt627f6ow.cloudfront.net", "localhost"); filter on this tag
 *                                to exclude localhost/dev sessions from the Clarity dashboard
 *   gitCommit                  — full commit SHA baked in at build time (GATSBY_GIT_COMMIT)
 *   gitBranch                  — branch name baked in at build time (GATSBY_GIT_BRANCH)
 *   gitCommitDate              — ISO 8601 commit timestamp baked in at build time (GATSBY_GIT_COMMIT_DATE)
 *   paymentDuration_ms         — ms elapsed from submit-click to success or failure (raw)
 *   paymentDurationBucket_success — "fast(<5s)" | "normal(5-15s)" | "slow(15-30s)" | "verySlow(30s+)"
 *   paymentDurationBucket_failure — same buckets, set on payment failure instead of success
 *   hydrationTime_ms     — ms from navigation start to React hydration complete (onInitialClientRender)
 *   hydrationTimeBucket  — "fast(<1s)" | "normal(1-3s)" | "slow(3-6s)" | "verySlow(6s+)"
 *   clsAtHydration       — CLS × 1000 accumulated up to hydration complete (integer); compare to
 *                          layoutShift_cls to see how much shift was caused by the SSR→client transition
 *   hasHydrationMismatch — "true" if React logged a hydration warning this session (visible flash/repaint)
 *
 * Events fired (no tag, just timeline markers):
 *   map_interaction    — bare canvas tap; prevents dead-click false positives
 *   map_marker_clicked — tap on a branch/location marker (high-intent)
 *   map_dragged        — pan gesture; signals user is searching for a branch
 *   api_error          — soft API error caught in the app (saveOrderError etc.)
 *   promo_code_submitted — promo code submitted by the user (optimistic, before server response)
 *   promo_code_applied   — promo code confirmed applied (no error returned by server)
 *   promo_code_failed  — promo code submission returned a server error
 *   promo_code_removed — applied promo code removed from the cart
 *   item_removed       — item removed from the cart
 *   payment_submitted  — user clicked the pay button (start of payment timing)
 *   gatsby_hydrated    — React hydration complete (onInitialClientRender); marks end of SSR→client window
 *   hydration_mismatch — React logged a hydration warning (content was re-rendered from scratch)
 */

// Null-safe wrapper — centralises the SSR + not-yet-loaded guard so individual
// functions don't need to repeat it. The init* functions that carry idempotency
// flags still check window.clarity directly so they don't mark themselves done
// before Clarity has actually loaded.
const clarity = (...args) => {
  if (typeof window !== "undefined" && window.clarity) {
    window.clarity(...args);
  }
};

// Forces full session recording for high-value sessions. Idempotent — Clarity
// ignores duplicate upgrade calls but we guard anyway to avoid noise.
let clarityUpgraded = false;
const upgradeClarity = (reason) => {
  if (clarityUpgraded) return;
  clarityUpgraded = true;
  clarity("upgrade", reason);
};

/**
 * Sets version and device-model tags for desktop sessions where MobileDetect
 * returns null (Windows, macOS, Linux, ChromeOS). os/browser names are skipped —
 * Clarity captures those automatically.
 *
 * Key names are OS/browser-specific so the value is self-explanatory without context:
 *   windowsVersion, macosVersion, chromeosVersion — OS version
 *   chromeVersion, edgeVersion, operaVersion, firefoxVersion, safariVersion — browser version
 *
 * Strategy 1 — Client Hints API (navigator.userAgentData):
 *   Supported by Chromium-based browsers (Chrome, Edge, Opera, Samsung Internet).
 *   platformVersion, uaFullVersion, and model require an async getHighEntropyValues()
 *   call — no user prompt appears; the async design is a privacy affordance.
 *   platform and brands are low-entropy and available synchronously — used to pick key names.
 *   NOTE: Windows 11 reports platformVersion "15.0.0" (not "11"); major >= 13 => Win11.
 *
 * Strategy 2 — UA regex fallback:
 *   Used for Firefox and Safari which do not implement userAgentData.
 *   Parses the classic navigator.userAgent string synchronously.
 */
const setClarityDesktopEnv = (ua) => {
  if (typeof window === "undefined") return;
  if (navigator.userAgentData) {
    // platform and brands are low-entropy — available synchronously, no getHighEntropyValues needed
    const platformName = (navigator.userAgentData.platform || "").toLowerCase();
    const osKey = platformName.includes("win") ? "windowsVersion"
      : platformName.includes("mac") ? "macosVersion"
      : platformName.includes("cros") ? "chromeosVersion"
      : "osVersion";

    // brands list includes Chromium + the real browser; find the non-Chromium brand
    const brands = navigator.userAgentData.brands || [];
    const mainBrand = brands.find((b) => !/chromium|not/i.test(b.brand));
    const browserKey = !mainBrand || /chrome/i.test(mainBrand.brand) ? "chromeVersion"
      : /edge/i.test(mainBrand.brand) ? "edgeVersion"
      : /opera|opr/i.test(mainBrand.brand) ? "operaVersion"
      : "browserVersion";

    navigator.userAgentData
      .getHighEntropyValues(["platformVersion", "uaFullVersion", "model"])
      .then(({ platformVersion, uaFullVersion, model }) => {
        if (platformVersion) clarity("set", osKey, platformVersion);
        if (uaFullVersion) clarity("set", browserKey, uaFullVersion);
        if (model) clarity("set", "device", model);
      })
      .catch(() => {});
    return;
  }

  // Regex fallback for Firefox and Safari.
  const osPatterns = [
    [/Windows NT ([\d.]+)/, "windowsVersion"],
    [/Mac OS X ([\d_.]+)/, "macosVersion"],
    [/CrOS [^ ]+ ([\d.]+)/, "chromeosVersion"],
    [/Linux/, null], // generic Linux has no meaningful version in the UA string
  ];
  for (const [regex, key] of osPatterns) {
    const match = ua.match(regex);
    if (match) {
      if (key && match[1]) clarity("set", key, match[1].replace(/_/g, "."));
      break;
    }
  }

  const browserPatterns = [
    [/Firefox\/([\d.]+)/, "firefoxVersion"],
    [/Edg\/([\d.]+)/, "edgeVersion"],
    [/OPR\/([\d.]+)/, "operaVersion"],
    [/Chrome\/([\d.]+)/, "chromeVersion"],
    [/Version\/([\d.]+).*Safari/, "safariVersion"],
    [/Trident\/.*rv:([\d.]+)/, "ieVersion"],   // IE 11
    [/MSIE ([\d.]+)/, "ieVersion"],             // IE 6–10
  ];
  for (const [regex, key] of browserPatterns) {
    const match = ua.match(regex);
    if (match) {
      clarity("set", key, match[1]);
      break;
    }
  }
};

/**
 * Registers global JS error handlers that tag the Clarity session whenever an
 * unhandled error or unhandled promise rejection occurs. This makes error sessions
 * filterable in the Clarity dashboard ("Filter by hasJsError = true") and marks
 * the exact moment of failure on the session timeline via clarity("event").
 *
 * Tags set on error:
 *   hasJsError   — "true" once any error fires; lets you filter the Clarity dashboard
 *                  for sessions that had at least one JS error
 *   jsErrorCount — running count of errors in this session
 *   lastError    — truncated message of the most recent error
 *   lastErrorUrl — script URL where the error originated (onerror only)
 *
 * LIMITATION: Clarity gives you the recording but not the stack trace. You can see
 * *what the user was doing* when the error happened, but diagnosing *why* still
 * requires reading minified stack traces manually.
 *
 * RECOMMENDED NEXT STEP — integrate Sentry or PostHog:
 *   Both tools capture full stack traces (with source-map deobfuscation), group
 *   duplicate errors, track error frequency across users, and support alerting.
 *   The key integration point: on each Sentry/PostHog error event, call
 *   window.clarity("event", "sentry_error") and attach the Sentry event ID as a
 *   Clarity tag — then from any Sentry error you can open the exact Clarity
 *   recording of that crash and watch a pixel-perfect replay of what led to it.
 *   This combination (Sentry for diagnosis + Clarity for replay) eliminates most
 *   "can't reproduce" bugs.
 */
let clarityInitialized = false;
let errorTrackingInitialized = false;
let performanceTrackingInitialized = false;
let staticContextInitialized = false;
let paymentSubmitTimestamp = null;
// Shared across initClarityPerformance (writer) and trackClarityHydrationComplete (reader)
// so the CLS snapshot at hydration time is accurate.
let clsScore = 0;
// Buffer for hydration data collected before Clarity finishes loading.
// Flushed by flushClarityHydrationData(), called from initClarity.
let hydrationPending = null;

// Resets all idempotency flags so the init functions can run again.
//
// Flags managed: clarityInitialized, errorTrackingInitialized,
//                performanceTrackingInitialized, staticContextInitialized.
//
// WHY THIS EXISTS — two scenarios where the flags get stuck:
//
//   1. Tests: Jest caches modules across tests in the same suite. Without a reset,
//      the second test that calls initClarity() silently skips because the flag from
//      the first test is still true. Call this in beforeEach() to get a clean slate.
//
//   2. Gatsby HMR (dev only): webpack may keep the old module instance alive during a
//      hot reload, leaving the flags set. The next componentDidMount call then skips
//      init, so no Clarity tags get applied until a full page refresh. module.hot.accept
//      below resets flags automatically so HMR sessions behave the same as fresh loads.
export const _resetClarityState = () => {
  clarityInitialized = false;
  errorTrackingInitialized = false;
  performanceTrackingInitialized = false;
  staticContextInitialized = false;
  paymentSubmitTimestamp = null;
  clsScore = 0;
  hydrationPending = null;
  clarityUpgraded = false;
};

if (typeof module !== "undefined" && module.hot) {
  module.hot.accept(() => _resetClarityState());
}

export const initClarityErrorTracking = () => {
  // Guard required: flag must not be set until Clarity is confirmed loaded,
  // otherwise a second call after Clarity loads would be skipped.
  if (typeof window === "undefined" || !window.clarity) return;
  if (errorTrackingInitialized) return;
  errorTrackingInitialized = true;

  let errorCount = 0;

  const reportError = (message, url) => {
    errorCount += 1;
    const errorBucket = errorCount === 1 ? "1" : errorCount <= 3 ? "2-3" : "4+";
    clarity("set", "hasJsError", "true");
    clarity("set", "jsErrorCount", String(errorCount));
    clarity("set", "jsErrorCountBucket", errorBucket);
    clarity("set", "lastError", String(message).slice(0, 200));
    if (url) clarity("set", "lastErrorUrl", url);
    clarity("event", "js_error");
  };

  window.addEventListener("error", (event) => {
    reportError(event.message, event.filename);
  });

  window.addEventListener("unhandledrejection", (event) => {
    const message = event.reason instanceof Error
      ? event.reason.message
      : String(event.reason);
    reportError(`UnhandledRejection: ${message}`);
  });
};

// Flushes buffered hydration data to Clarity once it's confirmed loaded.
// Called from initClarity so data is never lost regardless of load order.
const flushClarityHydrationData = () => {
  if (typeof window === "undefined" || !window.clarity) return;
  if (!hydrationPending) return;
  const { hydrationMs, clsAtHydrationInt, hasMismatch } = hydrationPending;
  hydrationPending = null;

  clarity("set", "hydrationTime_ms", String(hydrationMs));
  const bucket = hydrationMs < 1000 ? "fast(<1s)"
    : hydrationMs < 3000 ? "normal(1-3s)"
    : hydrationMs < 6000 ? "slow(3-6s)"
    : "verySlow(6s+)";
  clarity("set", "hydrationTimeBucket", bucket);
  clarity("set", "clsAtHydration", String(clsAtHydrationInt));
  if (hasMismatch) {
    clarity("set", "hasHydrationMismatch", "true");
    clarity("event", "hydration_mismatch");
  }
  clarity("event", "gatsby_hydrated");
};

/**
 * Sets static Clarity tags that don't depend on the user being logged in.
 * Called once from componentDidMount.
 */
export const initClarity = ({ business, user }) => {
  // Guard required: clarityInitialized flag must not be set before Clarity loads.
  if (typeof window === "undefined" || !window.clarity) return;
  if (clarityInitialized) return;
  clarityInitialized = true;

  clarity("set", "businessId", business.id);
  clarity("set", "businessName", business.name);

  const gitCommit = process.env.GATSBY_GIT_COMMIT;
  const gitBranch = process.env.GATSBY_GIT_BRANCH;
  const gitCommitDate = process.env.GATSBY_GIT_COMMIT_DATE;
  if (gitCommit) clarity("set", "gitCommit", gitCommit);
  if (gitBranch) clarity("set", "gitBranch", gitBranch);
  if (gitCommitDate) clarity("set", "gitCommitDate", gitCommitDate);

  // os/browser/deviceType are captured automatically by Clarity — only set model and version.
  // Mobile: MobileDetect gives the specific device model name and OS version.
  // Desktop: setClarityDesktopEnv uses Client Hints API (or UA regex fallback) for versions + model.
  const ua = window.navigator.userAgent;
  const md = new MobileDetect(ua);
  const device = md.phone() || md.tablet();
  if (device) {
    clarity("set", "device", device);
    const iosVersion = md.version("iOS");
    const androidVersion = md.version("Android");
    if (iosVersion) clarity("set", "iosVersion", String(iosVersion));
    else if (androidVersion) clarity("set", "androidVersion", String(androidVersion));
  } else {
    setClarityDesktopEnv(ua);
  }

  if (user.uniqueIdentifier) {
    clarity("set", "clientSessionId", user.uniqueIdentifier);
  }

  updateClarityUserIdentity(user);
  flushClarityHydrationData();
};

/**
 * Sets user-identity Clarity tags. Safe to call multiple times — Clarity overwrites
 * tags with the same key, so duplicate calls are harmless.
 *
 * Called from index.js in three situations to cover all login paths:
 *   1. componentDidMount (via initClarity) — user already logged in from persisted Redux state.
 *   2. componentWillReceiveProps, loggedIn false→true — fresh login during the session
 *      (verifyCode success or deep-link URL login).
 *   3. componentWillReceiveProps, loggedIn already true, secretGUID falsy→truthy — edge case
 *      where a persisted session is missing secretGUID (stored before it was introduced)
 *      and it arrives in a later props update.
 */
export const updateClarityUserIdentity = (user) => {
  if (!user.loggedIn) return;

  if (user.secretGUID) clarity("set", "backendSessionId", user.secretGUID);
  if (user.userId) {
    // identify() stitches recordings for the same user across separate browser sessions
    clarity("identify", String(user.userId), user.secretGUID || undefined);
    clarity("set", "appUserId", String(user.userId));
    upgradeClarity("user_logged_in");
  }
};

/**
 * Tags the app surface and device context. Called once on componentDidMount.
 *
 * appMode distinguishes four surfaces that have different UX flows and failure modes:
 *   "native"  — running inside the native mobile app (window.isNativeApp)
 *   "kiosk"   — in-store self-service kiosk (pageContext.kioskMode)
 *   "embed"   — loaded as an iframe inside another page (isEmbedMode)
 *   "web"     — standard browser session
 *
 * networkQuality comes from the Network Information API (navigator.connection).
 * Only available in Chromium; undefined in Firefox/Safari — guarded accordingly.
 * Useful for correlating slow-session complaints with poor connectivity.
 *
 * viewportSize helps reproduce layout bugs — a "phone" device can range from
 * 375px (iPhone SE) to 430px (iPhone 15 Pro Max). Screen resolution and
 * mobile/desktop classification are captured automatically by Clarity.
 *
 * entrySource is derived from URL params present at mount time:
 *   "deeplink" — secretGUID + userId in URL (user arriving via a shared/magic link)
 *   "qr"       — branchId in URL without user creds (typical QR-code table scan)
 *   "referral" — groupReferralUrl present
 *   "direct"   — none of the above
 */
export const initClarityAppContext = ({ location, kioskMode, isNative, embedMode }) => {
  if (typeof window === "undefined") return;

  const appMode = isNative ? "native"
    : kioskMode ? "kiosk"
    : embedMode ? "embed"
    : "web";
  clarity("set", "appMode", appMode);

  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (connection && connection.effectiveType) {
    clarity("set", "networkQuality", connection.effectiveType);
  }

  clarity("set", "viewportSize", `${window.innerWidth}x${window.innerHeight}`);
  clarity("set", "serverHost", window.location.hostname);

  const params = new URLSearchParams(location.search);
  const hasUserCreds = params.get("secretGUID") && params.get("userId");
  const hasBranchId = params.get("branchId");
  const hasReferral = params.get("groupReferralUrl");
  const entrySource = hasUserCreds ? "deeplink"
    : hasReferral ? "referral"
    : hasBranchId ? "qr"
    : "direct";
  clarity("set", "entrySource", entrySource);

  if (navigator.language) {
    clarity("set", "locale", navigator.language);
  }
};

/**
 * Updates branch and serving option tags when the user selects or changes a branch.
 * Called from initClarityStaticContext on mount (if a branch is already chosen) and
 * from componentDidUpdate whenever order.branchId changes.
 *
 * branchName is looked up from the pageContext branches list since the order
 * store only holds branchId. Both are set so recordings can be filtered by
 * either the ID (for programmatic cross-referencing) or the name (for humans
 * browsing the Clarity dashboard).
 */
export const updateClarityBranch = ({ branchId, servingOptionType, branches }) => {
  if (!branchId) return;

  clarity("set", "branchId", String(branchId));

  const branch = _.find(branches, { id: branchId });
  if (branch && branch.name) clarity("set", "branchName", branch.name);
  if (servingOptionType) clarity("set", "servingOption", servingOptionType);
};

/**
 * Initializes the one-time static context tags (app surface, entry source, funnel
 * stage, and initial branch). Guards on window.clarity and is idempotent so it can
 * be called from both componentDidMount and any retry path (e.g. componentDidUpdate)
 * without firing duplicate events.
 *
 * This exists because initClarityAppContext, updateClarityFunnelStage, and
 * updateClarityBranch use the null-safe clarity() wrapper — if window.clarity hasn't
 * loaded yet they silently no-op. Concentrating them here with a guard + flag ensures
 * the tags are set the first time Clarity is confirmed loaded.
 */
export const initClarityStaticContext = ({ location, kioskMode, isNative, embedMode, order, branches }) => {
  if (typeof window === "undefined" || !window.clarity) return;
  if (staticContextInitialized) return;
  staticContextInitialized = true;

  initClarityAppContext({ location, kioskMode, isNative, embedMode });
  updateClarityFunnelStage(location.pathname);
  if (order && order.branchId) {
    updateClarityBranch({ branchId: order.branchId, servingOptionType: order.servingOptionType, branches });
  }
};

/**
 * Maps the current pathname to a human-readable funnel stage and updates the
 * Clarity tag. Called from initClarityStaticContext on mount (initial stage) and
 * from componentDidUpdate whenever location.pathname changes.
 *
 * Having a semantic funnelStage tag (rather than raw pathnames) lets you filter
 * Clarity recordings to e.g. "all sessions that reached checkout" or "all sessions
 * that saw the payment screen" — which is far more useful for conversion analysis
 * than filtering by URL pattern.
 *
 * Stage mapping:
 *   home          — landing page "/"
 *   location      — branch/serving-option selection
 *   menu          — item browsing "/order/items"
 *   cart          — bag/checkout view "/order/bag" or "/order/checkout"
 *   payment       — payment method entry
 *   confirmation  — post-purchase event
 *   account       — my-account, profile, history
 *   gifts         — gifts / deals flow
 *   scan          — receipt/QR scanning
 *   other         — any route not matched above
 */
export const updateClarityFunnelStage = (pathname) => {
  const sw = (prefix) => pathname.startsWith(prefix);
  const stage =
    pathname === "/" ? "home"
    : sw("/serving-options") || sw("/find-location") ? "location"
    : sw("/order/items") ? "menu"
    : sw("/order/bag") || sw("/order/checkout") ? "cart"
    : sw("/payment-method") ? "payment"
    : sw("/purchase-event") ? "confirmation"
    : sw("/my-account") ? "account"
    : sw("/gifts") ? "gifts"
    : sw("/scan") || sw("/in-store-scan") ? "scan"
    : "other";

  clarity("set", "funnelStage", stage);
  clarity("event", `funnel_${stage}`);
};

// Google Core Web Vitals bucket helpers — boundaries match Google's good/needs-improvement/poor thresholds.
// Raw ms values are also sent alongside buckets: raw for reading recordings, bucket for dashboard filtering.
const serverResponseRating  = (ms) => ms < 800 ? "good(<800ms)"  : ms < 1800 ? "needsWork(800-1800ms)" : "poor(1800ms+)";
const firstPaintRating      = (ms) => ms < 1800 ? "good(<1.8s)"  : ms < 3000 ? "needsWork(1.8-3s)"    : "poor(3s+)";
const mainContentLoadRating = (ms) => ms < 2500 ? "good(<2.5s)"  : ms < 4000 ? "needsWork(2.5-4s)"    : "poor(4s+)";
const interactionDelayRating = (ms) => ms < 200 ? "good(<200ms)" : ms < 500  ? "needsWork(200-500ms)"  : "poor(500ms+)";
// CLS is stored ×1000 (integer); thresholds are 0.1 → 100, 0.25 → 250
const layoutShiftRating     = (x)  => x < 100  ? "good(<0.1)"   : x < 250   ? "needsWork(0.1-0.25)"   : "poor(0.25+)";

/**
 * Captures Web Vitals and page load timings from the browser's Performance API
 * and tags them onto the Clarity session. Called once after the load event fires
 * (or immediately if the page is already loaded).
 *
 * For each metric both the raw value (ms) and a Google-threshold rating are set:
 *   serverResponse_ttfb / serverResponseBucket_ttfb — Time to First Byte. good < 800ms.
 *   firstPaint_fcp      / firstPaintBucket_fcp      — First Contentful Paint. good < 1.8s.
 *   mainContentLoad_lcp / mainContentLoadBucket_lcp — Largest Contentful Paint. good < 2.5s.
 *   interactionDelay_inp / interactionDelayBucket_inp — Interaction to Next Paint. good < 200ms.
 *   layoutShift_cls     / layoutShiftBucket_cls     — Cumulative Layout Shift ×1000. good < 100 (= 0.1).
 *   domContentLoaded    — DOMContentLoaded ms (no bucket — no standard threshold).
 *
 * Use raw tags to read exact values in recordings; use bucket tags to filter the
 * Clarity dashboard (e.g. mainContentLoadBucket_lcp = "poor(4s+)").
 *
 * LCP is observed via PerformanceObserver because it is not in the navigation
 * timing entry — it fires asynchronously as the largest element is painted.
 */
export const initClarityPerformance = () => {
  // Guard required: performanceTrackingInitialized flag must not be set before Clarity loads.
  if (typeof window === "undefined" || !window.clarity || !window.performance) return;
  if (performanceTrackingInitialized) return;
  performanceTrackingInitialized = true;

  const report = () => {
    const nav = performance.getEntriesByType("navigation")[0];
    if (!nav) return;

    const ttfb = Math.round(nav.responseStart - nav.startTime);
    const domLoad = Math.round(nav.domContentLoadedEventEnd - nav.startTime);
    clarity("set", "serverResponse_ttfb", String(ttfb));
    clarity("set", "serverResponseBucket_ttfb", serverResponseRating(ttfb));
    clarity("set", "domContentLoaded", String(domLoad));

    const paintEntries = performance.getEntriesByType("paint");
    const fcp = _.find(paintEntries, { name: "first-contentful-paint" });
    if (fcp) {
      const fcpMs = Math.round(fcp.startTime);
      clarity("set", "firstPaint_fcp", String(fcpMs));
      clarity("set", "firstPaintBucket_fcp", firstPaintRating(fcpMs));
    }
  };

  if (document.readyState === "complete") {
    report();
  } else {
    window.addEventListener("load", report, { once: true });
  }

  if (typeof PerformanceObserver === "undefined") return;

  // LCP is not in navigation timing — must be observed via PerformanceObserver
  try {
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const last = entries[entries.length - 1];
      if (last) {
        const lcpMs = Math.round(last.startTime);
        clarity("set", "mainContentLoad_lcp", String(lcpMs));
        clarity("set", "mainContentLoadBucket_lcp", mainContentLoadRating(lcpMs));
      }
    });
    lcpObserver.observe({ type: "largest-contentful-paint", buffered: true });
  } catch (e) {} // entry type not supported in this browser

  // CLS is unitless (a ratio); multiply × 1000 and round so the raw tag is an integer for readability
  try {
    clsScore = 0; // reset module-level score in case initClarityPerformance is re-called after HMR
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!entry.hadRecentInput) clsScore += entry.value;
      }
      const clsInt = Math.round(clsScore * 1000);
      clarity("set", "layoutShift_cls", String(clsInt));
      clarity("set", "layoutShiftBucket_cls", layoutShiftRating(clsInt));
    });
    clsObserver.observe({ type: "layout-shift", buffered: true });
  } catch (e) {} // entry type not supported in this browser

  // INP — Interaction to Next Paint; take the worst observed interaction duration
  try {
    let maxInp = 0;
    const inpObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration > maxInp) {
          maxInp = entry.duration;
          const inpMs = Math.round(maxInp);
          clarity("set", "interactionDelay_inp", String(inpMs));
          clarity("set", "interactionDelayBucket_inp", interactionDelayRating(inpMs));
        }
      }
    });
    inpObserver.observe({ type: "event", buffered: true, durationThreshold: 40 });
  } catch (e) {} // entry type not supported in this browser
};

/**
 * Tracks a captcha execution failure on the Clarity session.
 * Call from any catch block where captcha throws or returns no token.
 *
 * Tags set:
 *   captchaFailed    — "true" once any captcha failure occurs
 *   captchaProvider  — "hcaptcha" | "google"
 *   captchaAction    — the action string passed to executeRecaptcha (e.g. "signup", "payment")
 *   captchaError     — truncated error message
 *
 * Event fired: captcha_failed — marks the exact moment on the session timeline.
 */
export const trackCaptchaFailure = (provider, action, error) => {
  clarity("set", "captchaFailed", "true");
  if (provider) clarity("set", "captchaProvider", provider);
  if (action) clarity("set", "captchaAction", action);
  if (error) clarity("set", "captchaError", String(error.message || error).slice(0, 200));
  clarity("event", "captcha_failed");
};

/**
 * Tags checkout initiation (begin-checkout equivalent).
 * Call when the user lands on the payment/checkout page.
 *
 *   checkoutTotal      — order total at this point
 *   checkoutItemCount  — number of line items
 *   checkoutCurrency   — ISO currency code
 *
 * Event fired: order_started
 */
export const trackClarityOrderStarted = ({ total, itemCount, currency }) => {
  if (total != null) clarity("set", "checkoutTotal", Number(total).toFixed(2));
  if (itemCount != null) {
    const bucket = itemCount === 1 ? "1" : itemCount <= 3 ? "2-3" : itemCount <= 6 ? "4-6" : "7+";
    clarity("set", "checkoutItemCount", String(itemCount));
    clarity("set", "checkoutItemCountBucket", bucket);
  }
  if (currency) clarity("set", "checkoutCurrency", currency);
  clarity("event", "order_started");
  upgradeClarity("checkout_started");
};

/**
 * Records the moment the user clicks the payment submit button.
 * Call at the top of onSubmit (after early-return guards) so the elapsed time to
 * success or failure can be bucketed in trackClarityOrderConfirmed / trackClarityPaymentFailed.
 *
 * Event fired: payment_submitted
 */
export const trackClarityPaymentSubmitted = () => {
  paymentSubmitTimestamp = Date.now();
  clarity("event", "payment_submitted");
};

const paymentDurationBucket = (ms) =>
  ms < 5000 ? "fast(<5s)" : ms < 15000 ? "normal(5-15s)" : ms < 30000 ? "slow(15-30s)" : "verySlow(30s+)";

/**
 * Tags a completed order after successful payment.
 * Call alongside firePurchaseEvent when orderPlaced transitions to true.
 *
 *   orderConfirmedId              — backend order ID
 *   orderConfirmedTotal           — final total charged
 *   orderConfirmedItemCount       — item count
 *   paymentDuration_ms            — ms from submit-click to success (raw)
 *   paymentDurationBucket_success — "fast(<5s)" | "normal(5-15s)" | "slow(15-30s)" | "verySlow(30s+)"
 *
 * Event fired: order_confirmed
 */
export const trackClarityOrderConfirmed = ({ orderId, total, itemCount, currency, paymentType }) => {
  if (orderId) clarity("set", "orderConfirmedId", String(orderId));
  if (total != null) clarity("set", "orderConfirmedTotal", Number(total).toFixed(2));
  if (itemCount != null) {
    const bucket = itemCount === 1 ? "1" : itemCount <= 3 ? "2-3" : itemCount <= 6 ? "4-6" : "7+";
    clarity("set", "orderConfirmedItemCount", String(itemCount));
    clarity("set", "orderConfirmedItemCountBucket", bucket);
  }
  if (currency) clarity("set", "orderConfirmedCurrency", currency);
  if (paymentType) clarity("set", "orderConfirmedPaymentType", String(paymentType));
  if (paymentSubmitTimestamp) {
    const ms = Date.now() - paymentSubmitTimestamp;
    clarity("set", "paymentDuration_ms", String(ms));
    clarity("set", "paymentDurationBucket_success", paymentDurationBucket(ms));
    paymentSubmitTimestamp = null;
  }
  clarity("event", "order_confirmed");
  upgradeClarity("order_confirmed");
};

/**
 * Tags a payment failure so the session can be filtered in Clarity dashboard.
 * Call when placeOrderError transitions from falsy to truthy.
 *
 *   paymentFailed       — "true" once any failure occurs
 *   paymentFailedReason — truncated error message
 *   paymentFailedType   — payment method type (e.g. "credit_card")
 *
 * Event fired: payment_failed — marks the exact moment on the session timeline.
 */
export const trackClarityPaymentFailed = ({ errorMessage, paymentType }) => {
  clarity("set", "paymentFailed", "true");
  if (errorMessage) clarity("set", "paymentFailedReason", String(errorMessage).slice(0, 200));
  if (paymentType) clarity("set", "paymentFailedType", String(paymentType));
  if (paymentSubmitTimestamp) {
    const ms = Date.now() - paymentSubmitTimestamp;
    clarity("set", "paymentDuration_ms", String(ms));
    clarity("set", "paymentDurationBucket_failure", paymentDurationBucket(ms));
    paymentSubmitTimestamp = null;
  }
  clarity("event", "payment_failed");
};

/**
 * Tags the most recent menu search term (≥2 chars) so sessions can be filtered
 * by what users searched for. The input is already unmasked in Clarity recordings;
 * this tag makes search a first-class filterable dimension in the dashboard.
 *
 * Call after the debounced search fires (when results are computed).
 *
 * Event fired: menu_searched
 */
export const trackClaritySearchPerformed = (term) => {
  if (!term || term.length < 2) return;
  clarity("set", "lastSearchTerm", String(term).slice(0, 100));
  clarity("event", "menu_searched");
};

/**
 * Fires a Clarity event when a menu item is added to the cart.
 * Lets you filter for "sessions that added an item" and correlates cart
 * interactions with funnel drop-off.
 *
 * Tags set (all optional):
 *   lastAddedItem          — name of the item added (for "what did users add?" analysis)
 *   lastAddedItemPrice     — price of the item (for basket-value segmentation)
 *   lastAddedItemCurrency  — ISO currency code
 *
 * Event fired: item_added_to_cart
 */
export const trackClarityItemAdded = ({ itemName, price, currency } = {}) => {
  if (itemName) clarity("set", "lastAddedItem", String(itemName).slice(0, 100));
  if (price != null) clarity("set", "lastAddedItemPrice", String(price));
  if (currency) clarity("set", "lastAddedItemCurrency", currency);
  clarity("event", "item_added_to_cart");
  upgradeClarity("item_added");
};

/**
 * Registers a visibilitychange handler that tags the session when the user
 * switches away from the tab while on the payment page. A tab-switch during
 * checkout is a strong signal of hesitation or comparison-shopping and
 * correlates with abandoned orders.
 *
 * Call from PaymentView componentDidMount; the handler is registered once
 * and auto-removed when the component unmounts (call the returned cleanup fn).
 *
 * Tag set:   tabHiddenDuringCheckout — "true" if the tab was hidden at any point
 * Event fired: tab_hidden_during_checkout
 *
 * Returns a cleanup function to pass to componentWillUnmount.
 */
export const initClarityCheckoutVisibility = () => {
  if (typeof window === "undefined") return () => {};

  let fired = false;
  const handler = () => {
    if (document.visibilityState === "hidden" && !fired) {
      fired = true;
      clarity("set", "tabHiddenDuringCheckout", "true");
      clarity("event", "tab_hidden_during_checkout");
    }
  };
  document.addEventListener("visibilitychange", handler);
  return () => document.removeEventListener("visibilitychange", handler);
};

/**
 * Tags the session when the user has been on the verify-code screen for >30s
 * without submitting — a reliable proxy for "SMS not received yet".
 * Call from a setTimeout in VerifyCodeView componentDidMount.
 */
export const trackClaritySmsDelayed = () => {
  clarity("event", "sms_code_delayed");
  clarity("set", "smsCodeDelayed", "true");
};

/**
 * Tags the session when the user taps "Resend".
 */
export const trackClaritySmsResend = () => {
  clarity("event", "sms_resend_requested");
  clarity("set", "smsResendRequested", "true");
};

/**
 * Tags the session when the user taps "Change phone number" — distinct from a resend
 * because the user is abandoning verification, not requesting another SMS.
 */
export const trackClaritySmsChangePhone = () => {
  clarity("event", "sms_change_phone");
  clarity("set", "smsChangePhoneRequested", "true");
};

/**
 * Fires Clarity events for Google Map interactions, preventing dead-click false positives.
 * The map SDK handles clicks internally without DOM mutations, which Clarity would
 * otherwise interpret as "click with no response".
 *
 *   map_marker_clicked — tap on a branch/location marker (high-intent; use for funnel analysis)
 *   map_dragged        — pan gesture; signals the user is searching but hasn't found a branch yet
 *   map_interaction    — bare canvas tap; no analytical value beyond dead-click suppression
 */
export const trackClarityMapInteraction = () => clarity("event", "map_interaction");
export const trackClarityMapMarkerClicked = () => clarity("event", "map_marker_clicked");
export const trackClarityMapDragged = () => clarity("event", "map_dragged");

/**
 * Tags a caught (soft) API error — errors that don't surface as unhandled JS exceptions
 * but indicate a failed server operation (e.g. saveOrderError).
 *
 * Tags set:
 *   apiHasError         — "true" once any caught API error occurs
 *   lastApiErrorContext — which flow triggered it (up to 100 chars)
 *   lastApiError        — truncated error message (200 chars)
 *
 * Event fired: api_error
 */
export const trackClarityApiError = (context, error) => {
  clarity("set", "apiHasError", "true");
  clarity("set", "lastApiErrorContext", String(context || "").slice(0, 100));
  clarity("set", "lastApiError", String(error || "").slice(0, 200)); // Clarity tag cap is 256 chars
  clarity("event", "api_error");
};

/**
 * Tracks the promo code lifecycle: submission, server-side failure, and removal.
 *
 * trackClarityPromoCodeSubmitted — called when the user hits "Apply" (before server response)
 * trackClarityPromoCodeApplied   — called when the server confirms the code (no error)
 * trackClarityPromoCodeFailed    — called when the server returns an error for that code
 * trackClarityPromoCodeRemoved   — called when the user removes an applied code
 *
 * Tags set:
 *   promoCodeSubmitted — "true" once any submission fires (regardless of outcome)
 *   promoCodeApplied   — "true" once a code is confirmed applied (no error)
 *   promoCodeFailed    — "true" if any submission returned an error
 *   promoCodeRemoved   — "true" once any code is removed from the cart
 *   lastPromoCode      — most recently submitted code (set by submitted/failed; applied reuses it)
 *   lastPromoError     — error type key (e.g. "INVALID_CODE", up to 50 chars)
 *
 * Events fired: promo_code_submitted | promo_code_applied | promo_code_failed | promo_code_removed
 */
export const trackClarityPromoCodeSubmitted = (code) => {
  clarity("set", "promoCodeSubmitted", "true");
  clarity("set", "lastPromoCode", String(code || "").slice(0, 100));
  clarity("event", "promo_code_submitted");
};

export const trackClarityPromoCodeApplied = () => {
  clarity("set", "promoCodeApplied", "true");
  clarity("event", "promo_code_applied");
};

export const trackClarityPromoCodeFailed = (code, errorType) => {
  clarity("set", "promoCodeFailed", "true");
  clarity("set", "lastPromoCode", String(code || "").slice(0, 100));
  clarity("set", "lastPromoError", String(errorType || "").slice(0, 50));
  clarity("event", "promo_code_failed");
};

export const trackClarityPromoCodeRemoved = (code) => {
  clarity("set", "promoCodeRemoved", "true");
  clarity("set", "lastPromoCode", String(code || "").slice(0, 100));
  clarity("event", "promo_code_removed");
};

/**
 * Tags the session when an item is removed from the cart. Mirrors the shape of
 * trackClarityItemAdded so both can be compared in the dashboard.
 *
 * Tags set:
 *   lastRemovedItem         — item name (up to 100 chars)
 *   lastRemovedItemPrice    — item price
 *   lastRemovedItemCurrency — ISO currency code
 *
 * Event fired: item_removed
 */
export const trackClarityItemRemoved = ({ itemName, price, currency } = {}) => {
  if (itemName) clarity("set", "lastRemovedItem", String(itemName).slice(0, 100));
  if (price != null) clarity("set", "lastRemovedItemPrice", String(price));
  if (currency) clarity("set", "lastRemovedItemCurrency", currency);
  clarity("event", "item_removed");
};

/**
 * Sets customerType to "new" or "returning" once latestOrders has loaded.
 * Call when user.latestOrders transitions from undefined/loading to an array.
 *
 * Tag set: customerType — "new" | "returning"
 */
export const updateClarityCustomerType = (latestOrders) => {
  if (!Array.isArray(latestOrders)) return;
  clarity("set", "customerType", latestOrders.length > 0 ? "returning" : "new");
};

/**
 * Patches console.error to detect React hydration mismatch warnings before Clarity loads.
 * Call from gatsby-browser.js onClientEntry — must run before React hydrates.
 *
 * React hydration warnings fire synchronously during the initial render pass.
 * Because Clarity loads async, it is often not available yet at that point.
 * This function buffers the detection on window.__clarityHydrationMismatch so
 * trackClarityHydrationComplete can read it later (regardless of load order).
 *
 * React 17: warns via console.error("Warning: Did not expect server HTML to contain...")
 * React 18: calls onRecoverableError — both paths trigger console.error in practice.
 */
export const initClarityHydrationMismatchDetector = () => {
  if (typeof window === "undefined") return;
  const orig = console.error;
  console.error = (...args) => {
    const msg = typeof args[0] === "string" ? args[0] : "";
    if (/did not expect server html|hydration failed|hydrating|server-rendered html/i.test(msg)) {
      window.__clarityHydrationMismatch = true;
    }
    orig.apply(console, args);
  };
};

/**
 * Records the SSR→client hydration transition. Call from gatsby-browser.js onInitialClientRender.
 *
 * Captures:
 *   hydrationTime_ms    — ms from navigation start to hydration complete
 *   hydrationTimeBucket — "fast(<1s)" | "normal(1-3s)" | "slow(3-6s)" | "verySlow(6s+)"
 *   clsAtHydration      — CLS × 1000 accumulated up to this point (compare to layoutShift_cls
 *                         to isolate how much layout shift was caused by the hydration transition)
 *   hasHydrationMismatch — "true" if React logged a hydration warning (content was re-rendered)
 *
 * Events fired:
 *   gatsby_hydrated    — timeline marker for end of SSR→client window
 *   hydration_mismatch — only if a mismatch was detected
 *
 * Stores data in hydrationPending and tries an immediate flush. If Clarity isn't loaded
 * yet, flushClarityHydrationData() (called from initClarity) will flush it later.
 */
export const trackClarityHydrationComplete = () => {
  if (typeof window === "undefined" || typeof performance === "undefined") return;

  hydrationPending = {
    hydrationMs: Math.round(performance.now()),
    clsAtHydrationInt: Math.round(clsScore * 1000),
    hasMismatch: !!window.__clarityHydrationMismatch,
  };
  flushClarityHydrationData();
};
